/**
 * Storage-Backed Logger
 *
 * Extends the production-safe logger to write logs to Supabase storage
 * Batches log entries and writes them periodically to reduce API calls
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import { LOG_LEVEL_NAMES, LoggableData, LogLevel } from '@xanectech/types';

const logger = createLogger('Auth - storageLogger');

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  component: string;
  message: string;
  data?: unknown;
}

interface LogBatch {
  userId: string;
  component: string;
  entries: LogEntry[];
  startTime: Date;
}

class StorageLogger {
  private batches: Map<string, LogBatch> = new Map();
  private flushInterval: number = 60000; // 1 minute
  private maxBatchSize: number = 100; // Max entries before force flush
  private flushTimer: number | null = null;
  private enabled: boolean = false;

  constructor() {
    // Start flush timer
    this.startFlushTimer();

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        void this.flushAllBatches(true);
      });
    }
  }

  /**
   * Enable storage logging for authenticated users only
   */
  async enable(): Promise<void> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        this.enabled = true;
        createLogger('StorageLogger').info('Storage logging enabled');
      }
    } catch (error) {
      logger.error('Failed to enable storage logging:', error as Error);
    }
  }

  /**
   * Disable storage logging and flush remaining logs
   */
  async disable(): Promise<void> {
    this.enabled = false;
    await this.flushAllBatches(true);
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Add log entry to batch
   */
  addEntry(
    userId: string,
    component: string,
    level: LogLevel,
    message: string,
    data?: unknown,
  ): void {
    if (!this.enabled) return;

    const batchKey = `${userId}:${component}`;
    let batch = this.batches.get(batchKey);

    if (!batch) {
      batch = {
        userId,
        component,
        entries: [],
        startTime: new Date(),
      };
      this.batches.set(batchKey, batch);
    }

    batch.entries.push({
      timestamp: new Date().toISOString(),
      level,
      component,
      message,
      data,
    });

    // Force flush if batch is too large
    if (batch.entries.length >= this.maxBatchSize) {
      void this.flushBatch(batchKey);
    }
  }

  /**
   * Start automatic flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = window.setInterval(() => {
      void this.flushAllBatches(false);
    }, this.flushInterval) as unknown as number;
  }

  /**
   * Flush specific batch to storage
   */
  private async flushBatch(batchKey: string): Promise<void> {
    const batch = this.batches.get(batchKey);
    if (!batch || batch.entries.length === 0) return;

    try {
      // Create log file content
      const logContent = JSON.stringify(
        {
          component: batch.component,
          startTime: batch.startTime.toISOString(),
          endTime: new Date().toISOString(),
          entries: batch.entries,
        },
        null,
        2,
      );

      const fileName = `${batch.userId}/${batch.component}_${Date.now()}.json`;
      const blob = new Blob([logContent], { type: 'application/json' });

      // Upload to storage

      const { data, error } = await supabase.storage
        .from('logs')
        .upload(fileName, blob, {
          contentType: 'application/json',
          upsert: false,
        });

      if (error) {
        logger.error('Failed to upload log file:', error);
        return;
      }

      // Create metadata entry
      const { error: metaError } = await supabase.from('log_metadata').insert({
        user_id: batch.userId,
        log_file_path: fileName,
        log_type: this.getMostSevereLevel(batch.entries),
        component: batch.component,
        start_time: batch.startTime.toISOString(),
        end_time: new Date().toISOString(),
        entry_count: batch.entries.length,
        file_size: blob.size,
      });

      if (metaError) {
        logger.error('Failed to create log metadata:', metaError);
      }

      // Clear batch
      this.batches.delete(batchKey);
    } catch (error) {
      logger.error('Error flushing batch:', error as Error);
    }
  }

  /**
   * Flush all batches
   */
  private async flushAllBatches(synchronous: boolean): Promise<void> {
    const batchKeys = Array.from(this.batches.keys());

    if (synchronous) {
      // On page unload, just flush what we can
      // Note: Storage API doesn't support synchronous operations
      // Logs may be lost on page close (acceptable tradeoff)
      for (const key of batchKeys) {
        await this.flushBatch(key).catch((err) => {
          logger.error('Error flushing batch on unload:', {
            error: err as string,
          });
        });
      }
      this.batches.clear();
    } else {
      // Async flush
      await Promise.all(batchKeys.map((key) => this.flushBatch(key)));
    }
  }

  /**
   * Get most severe log level from entries
   */
  private getMostSevereLevel(entries: LogEntry[]): string {
    const severity: Record<LogLevel, number> = {
      [LogLevel.DEBUG]: 1,
      [LogLevel.INFO]: 2,
      [LogLevel.WARN]: 3,
      [LogLevel.ERROR]: 4,
    };

    let maxSeverity = 0;
    let maxLevel: LogLevel = LogLevel.DEBUG;

    for (const entry of entries) {
      const level = severity[entry.level] || 0;
      if (level > maxSeverity) {
        maxSeverity = level;
        maxLevel = entry.level;
      }
    }

    // Convert enum to string name for database
    return LOG_LEVEL_NAMES[maxLevel].toLowerCase();
  }

  /**
   * Get batch statistics
   */
  getStats(): {
    batches: number;
    totalEntries: number;
    enabled: boolean;
  } {
    let totalEntries = 0;
    for (const batch of this.batches.values()) {
      totalEntries += batch.entries.length;
    }

    return {
      batches: this.batches.size,
      totalEntries,
      enabled: this.enabled,
    };
  }
}

// Singleton instance
export const storageLogger = new StorageLogger();

/**
 * Create a logger that writes to both console and storage
 */
export const createStorageLogger = (component: string) => {
  const consoleLogger = createLogger(component);
  const toMetadata = (
    value: unknown,
  ): Record<string, LoggableData> | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const obj = value as Record<string, unknown>;
    const result: Record<string, LoggableData> = {};
    for (const [key, val] of Object.entries(obj)) {
      if (
        val === null ||
        val === undefined ||
        typeof val === 'string' ||
        typeof val === 'number' ||
        typeof val === 'boolean'
      ) {
        result[key] = val as LoggableData;
      } else if (typeof val === 'object') {
        result[key] = JSON.stringify(val) as LoggableData;
      } else {
        result[key] = String(val) as LoggableData;
      }
    }
    return result;
  };

  return {
    debug: (message: string, data?: unknown) => {
      consoleLogger.debug(message, toMetadata(data));

      // Write to storage if enabled
      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user && typeof userData.user.id === 'string') {
            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.DEBUG,
              message,
              toMetadata(data),
            );
          }
        })
        .catch(() => {});
    },

    info: (message: string, data?: unknown) => {
      consoleLogger.info(message, toMetadata(data));

      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user && typeof userData.user.id === 'string') {
            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.INFO,
              message,
              toMetadata(data),
            );
          }
        })
        .catch(() => {});
    },

    warn: (message: string, data?: unknown) => {
      consoleLogger.warn(message, toMetadata(data));

      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user && typeof userData.user.id === 'string') {
            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.WARN,
              message,
              toMetadata(data),
            );
          }
        })
        .catch(() => {});
    },

    error: (message: string, error?: Error | unknown, data?: unknown) => {
      const errorObj = error instanceof Error ? error : undefined;
      const metadata = error instanceof Error ? undefined : toMetadata(error);
      consoleLogger.error(message, errorObj || metadata);

      supabase.auth
        .getUser()
        .then(({ data: userData }) => {
          if (userData.user && typeof userData.user.id === 'string') {
            const errorData =
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                  }
                : error;

            storageLogger.addEntry(
              userData.user.id as string,
              component,
              LogLevel.ERROR,
              message,
              { error: errorData, ...(data as Record<string, unknown>) },
            );
          }
        })
        .catch(() => {});
    },

    performance: (operation: string, startTime: number) => {
      const duration = performance.now() - startTime;
      consoleLogger.info(
        `Performance: ${operation}`,
        { duration: Math.round(duration * 100) / 100, unit: 'ms' },
        'performance',
      );
    },

    userAction: (action: string, data?: unknown) => {
      const metadata =
        data && typeof data === 'object' && Object.keys(data).length > 0
          ? { data: data as Record<string, unknown> }
          : undefined;
      consoleLogger.info(`User Action: ${action}`, metadata);
    },
  };
};

/**
 * Initialize storage logger when user logs in
 */
export const initializeStorageLogger = async (): Promise<void> => {
  await storageLogger.enable();
};

/**
 * Clean up storage logger when user logs out
 */
export const cleanupStorageLogger = async (): Promise<void> => {
  await storageLogger.disable();
};
